import type { ReactNode } from 'react';
import type {
  AdminOrderBy,
  AdminSearchContentType,
  AdminSearchResultContentSource,
  AdminSearchResultItem,
  AdminSortBy,
} from '../types';
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQueryParam } from '~/hooks/useQueryParam'; // Export directly instead of from index to avoid circular dependency
import { useDebouncedValue } from '~/hooks/useDebouncedValue'; // Export directly instead of from index to avoid circular dependency
import { useGetInternalUsersQuery } from '~/features/common/hooks';
import { useGetSkills } from '~/elc/features/skills/api/getSkills';
import { useAdminSearchResults, useGetAdminObjectTags } from '../api';
import {
  ADMIN_ORDER_KEYS,
  ADMIN_SORT_KEYS,
  AdminOrderByOptions,
  AdminSearchParamOptions,
  AdminSearchUpdateDate,
  AdminSortByOptions,
  ALGOLIA_SEARCH_DEBOUNCE_MS,
  DEFAULT_ADMIN_SORT_BY,
  DEFAULT_ADMIN_SORT_ORDER,
  TAG_LIMIT,
  SKILL_LIMIT,
} from '../constants';

export type AdminSearchContextValue = {
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  searchResults: AdminSearchResultItem[];
  isLoading: boolean;
  isError: boolean;
  errorStatus?: number;
  hasNextPage: boolean;
  fetchNextPage: () => void;
  isFetchingNextPage: boolean;
  totalCount: number;
  currentItemInItemPane: any;
  setCurrentItemInItemPane: (item: any) => void;
  sortBy: AdminSortBy;
  setSortBy: (sortBy: AdminSortBy) => void;
  sortOrder: AdminOrderBy;
  setSortOrder: (sortOrder: AdminOrderBy) => void;
  selectedTags: string[];
  setSelectedTags: (tags: string[]) => void;
  tagSearchTerm: string;
  setTagSearchTerm: (term: string) => void;
  availableTags: any[];
  isLoadingTags: boolean;
  selectedSkills: string[];
  setSelectedSkills: (skills: string[]) => void;
  skillSearchTerm: string;
  setSkillSearchTerm: (term: string) => void;
  availableSkills: any[];
  isLoadingSkills: boolean;
  contentTypes: AdminSearchContentType[];
  setContentTypes: (types: AdminSearchContentType[]) => void;
  contentSources: AdminSearchResultContentSource[];
  setContentSources: (sources: AdminSearchResultContentSource[]) => void;
  filterUpdatedDate: AdminSearchUpdateDate;
  updateFilterUpdatedDate: (date: AdminSearchUpdateDate) => void;
  selectedUpdatedByUserIds: string[];
  setSelectedUpdatedByUserIds: (userIds: string[]) => void;
  updatedBySearchTerm: string;
  setUpdatedBySearchTerm: (term: string) => void;
  updatedByUsers: any[];
  isErrorUpdatedByUsers: boolean;
  resetFilters: () => void;
  areFiltersActive: boolean;
};

type AdminSearchProviderProps = {
  children: ReactNode;
};

export const AdminSearchContext = createContext<AdminSearchContextValue | null>(null);

export const AdminSearchProvider = ({ children }: AdminSearchProviderProps) => {
  const [searchQuery, setSearchQuery] = useQueryParam<string>(AdminSearchParamOptions.QUERY, '');
  const debouncedSearchQuery = useDebouncedValue(searchQuery, ALGOLIA_SEARCH_DEBOUNCE_MS);
  const [currentItemInItemPane, setCurrentItemInItemPane] = useState<any>(null);
  const isEmpty = !debouncedSearchQuery.trim();

  const [sortBy, setSortBy] = useQueryParam<AdminSortBy>(AdminSearchParamOptions.SORT_BY, DEFAULT_ADMIN_SORT_BY);
  const [sortOrder, setSortOrder] = useQueryParam<AdminOrderBy>(
    AdminSearchParamOptions.SORT_ORDER,
    DEFAULT_ADMIN_SORT_ORDER,
  );

  // ContentType filter
  const [contentTypes, setContentTypes] = useQueryParam<AdminSearchContentType[]>(AdminSearchParamOptions.TYPES, []);
  const [contentSources, setContentSources] = useQueryParam<AdminSearchResultContentSource[]>(
    AdminSearchParamOptions.CONTENT_SOURCES,
    [],
  );

  // Tags filter
  const [selectedTags, setSelectedTags] = useQueryParam<string[]>(AdminSearchParamOptions.TAGS, []);
  const [tagSearchTerm, setTagSearchTerm] = useState('');
  const debouncedTagSearchTerm = useDebouncedValue(tagSearchTerm, ALGOLIA_SEARCH_DEBOUNCE_MS);
  const tagsQuery = useGetAdminObjectTags({
    sortBy: 'popularity',
    limit: TAG_LIMIT,
    searchTerm: debouncedTagSearchTerm,
  });

  // Skills filter
  const [selectedSkills, setSelectedSkills] = useQueryParam<string[]>(AdminSearchParamOptions.SKILLS, []);
  const [skillSearchTerm, setSkillSearchTerm] = useState('');
  const getSkillsQuery = useGetSkills(skillSearchTerm, SKILL_LIMIT);

  // UpdatedDate filter
  const [filterUpdatedDate, setFilterUpdatedDate] = useQueryParam<AdminSearchUpdateDate>(
    AdminSearchParamOptions.UPDATED_DATE,
    AdminSearchUpdateDate.ALL_TIME,
  );
  const [updatedDateTouched, setUpdatedDateTouched] = useState(false);
  const updateFilterUpdatedDate = useCallback(
    (date: AdminSearchUpdateDate) => {
      setUpdatedDateTouched(true);
      setFilterUpdatedDate(date);
    },
    [setFilterUpdatedDate],
  );

  // UpdatedBy filter
  const [selectedUpdatedByUserIds, setSelectedUpdatedByUserIds] = useQueryParam<string[]>(
    AdminSearchParamOptions.UPDATED_BY,
    [],
  );
  const [updatedBySearchTerm, setUpdatedBySearchTerm] = useState('');
  const debouncedUpdatedBySearchTerm = useDebouncedValue(updatedBySearchTerm, ALGOLIA_SEARCH_DEBOUNCE_MS);
  const updatedByFilterQuery = useGetInternalUsersQuery({
    searchTerm: debouncedUpdatedBySearchTerm || '',
    excludedUserIds: selectedUpdatedByUserIds || [],
    enabled: Boolean(debouncedUpdatedBySearchTerm) || (selectedUpdatedByUserIds?.length ?? 0) > 0,
  });

  // Sanitize URL sort parameters
  useEffect(() => {
    if (sortBy !== DEFAULT_ADMIN_SORT_BY || sortOrder !== DEFAULT_ADMIN_SORT_ORDER) {
      if (!ADMIN_SORT_KEYS.includes(sortBy)) {
        setSortBy(AdminSortByOptions.RELEVANCE);
      }
      if (!ADMIN_ORDER_KEYS.includes(sortOrder)) {
        setSortOrder(AdminOrderByOptions.DESC);
      }
      // Special case for RELEVANCE sort option, since there is only one order option
      if (sortBy === AdminSortByOptions.RELEVANCE) {
        setSortOrder(AdminOrderByOptions.DESC);
      }
    }
  }, [setSortBy, setSortOrder, sortBy, sortOrder]);

  // Reset all filters
  const resetFilters = useCallback(() => {
    setContentTypes([]);
    setContentSources([]);
    setSelectedTags([]);
    setSelectedSkills([]);
    setFilterUpdatedDate(AdminSearchUpdateDate.ALL_TIME);
    setSelectedUpdatedByUserIds([]);
  }, [
    setContentTypes,
    setContentSources,
    setSelectedTags,
    setSelectedSkills,
    setFilterUpdatedDate,
    setSelectedUpdatedByUserIds,
  ]);

  // Check if any filters are active
  const areFiltersActive = useMemo(
    () =>
      Boolean(
        contentTypes.length ||
          contentSources.length ||
          selectedTags.length ||
          selectedSkills.length ||
          updatedDateTouched ||
          selectedUpdatedByUserIds.length,
      ),
    [
      contentTypes.length,
      contentSources.length,
      selectedTags.length,
      selectedSkills.length,
      updatedDateTouched,
      selectedUpdatedByUserIds.length,
    ],
  );

  const shouldEnableSearch = useMemo(() => !isEmpty || areFiltersActive, [isEmpty, areFiltersActive]);
  const searchApiResult = useAdminSearchResults(
    {
      query: debouncedSearchQuery,
      tags: selectedTags,
      skills: selectedSkills,
      types: contentTypes,
      contentSources,
      updatedDate: filterUpdatedDate,
      updatedBy: selectedUpdatedByUserIds,
    },
    shouldEnableSearch,
  );

  const value = useMemo(() => {
    // Use first page for metadata (totals should be consistent across all pages)
    const firstPage = searchApiResult.data?.pages?.[0];
    const folderTotal = firstPage?.pagination?.folders?.total ?? 0;
    const contentTotal = firstPage?.pagination?.content?.total ?? 0;
    const totalCount = folderTotal + contentTotal;

    // Show loading state when:
    // 1. Search api call is loading AND search query is not empty (normal search)
    // 2. OR when filters are active (even if search query is empty) and api call is loading (filter search)
    const isResultLoading = (searchApiResult.isLoading && !isEmpty) || (searchApiResult.isLoading && areFiltersActive);

    return {
      searchQuery,
      setSearchQuery,
      searchResults: searchApiResult.data?.pages?.map((page) => page.data).flat() || [],
      isLoading: isResultLoading,
      isError: searchApiResult.isError,
      errorStatus: searchApiResult?.error?.status,
      hasNextPage: Boolean(searchApiResult.hasNextPage),
      fetchNextPage: searchApiResult.fetchNextPage,
      isFetchingNextPage: searchApiResult.isFetchingNextPage,
      totalCount,
      currentItemInItemPane,
      setCurrentItemInItemPane,
      sortBy,
      setSortBy,
      sortOrder,
      setSortOrder,
      selectedTags,
      setSelectedTags,
      tagSearchTerm,
      setTagSearchTerm,
      availableTags: tagsQuery.data || [],
      isLoadingTags: tagsQuery.isLoading,
      selectedSkills,
      setSelectedSkills,
      skillSearchTerm,
      setSkillSearchTerm,
      availableSkills: getSkillsQuery.data?.skills || [],
      isLoadingSkills: getSkillsQuery.isLoading,
      contentTypes,
      setContentTypes,
      contentSources,
      setContentSources,
      filterUpdatedDate,
      updateFilterUpdatedDate,
      selectedUpdatedByUserIds,
      setSelectedUpdatedByUserIds,
      updatedBySearchTerm,
      setUpdatedBySearchTerm,
      updatedByUsers: updatedByFilterQuery.data || [],
      isErrorUpdatedByUsers: updatedByFilterQuery.isError,
      resetFilters,
      areFiltersActive,
    };
  }, [
    searchQuery,
    setSearchQuery,
    searchApiResult,
    currentItemInItemPane,
    setCurrentItemInItemPane,
    isEmpty,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    selectedTags,
    setSelectedTags,
    tagSearchTerm,
    setTagSearchTerm,
    tagsQuery,
    selectedSkills,
    setSelectedSkills,
    skillSearchTerm,
    setSkillSearchTerm,
    getSkillsQuery,
    contentTypes,
    setContentTypes,
    contentSources,
    setContentSources,
    filterUpdatedDate,
    updateFilterUpdatedDate,
    selectedUpdatedByUserIds,
    setSelectedUpdatedByUserIds,
    updatedBySearchTerm,
    setUpdatedBySearchTerm,
    updatedByFilterQuery,
    resetFilters,
    areFiltersActive,
  ]);

  return <AdminSearchContext.Provider value={value}>{children}</AdminSearchContext.Provider>;
};

export const useAdminSearch = () => {
  const context = useContext(AdminSearchContext);
  if (!context) {
    throw new Error('useAdminSearch must be used within an AdminSearchProvider');
  }
  return context;
};
